// TELEMETRY-GATE: F1 satisfied at the init site (`src/utils/tracing.ts`
// returns early on `!isSaasTelemetryEnabled`, so `datadogRum.init` never
// runs); `addError` is a no-op when RUM is uninitialised.
import { datadogRum } from '@datadog/browser-rum';
import { classifyBoundaryError } from './boundary-error-classify';

/** Boundaries that replace the whole app or page when they catch. */
export type FatalBoundaryName =
  | 'app_root'
  | 'app_intl_shell'
  | 'dictate_onboarding'
  | 'minimal_root';

/** Boundaries that contain a failure and keep the rest of the page alive. */
export type LocalBoundaryName =
  | 'rich_text_editor'
  | 'settings_modal'
  | 'hero_health'
  | 'presentation_panel'
  | 'template_builder_panel'
  | 'form_template_panel'
  | 'lazy_chunk';

type BoundaryErrorReport = {
  /**
   * Extra context, merged under the boundary fields. Bounded values only —
   * never a message, URL or anything a user typed.
   */
  context?: Record<string, string | number | boolean>;
} & (
  | {
      boundary: FatalBoundaryName;
      fatal: true;
      /** The root boundary is reloading the tab, so the user never sees the fallback. */
      recoveredByReload?: boolean;
    }
  | { boundary: LocalBoundaryName; fatal: false; recoveredByReload?: never }
);

/**
 * V8 opens the stack with a `Name: message` header; other engines have no
 * header. Only that header is rewritten: a short message such as `1` or `at`
 * also matches inside frames, and changing those breaks source mapping.
 */
const redactStackHeader = (
  stack: string,
  message: string,
  replacement: string
): string => {
  if (!message) return stack;
  const at = stack.indexOf(`: ${message}`);
  if (at < 0 || stack.slice(0, at).includes('\n')) return stack;
  return replacement + stack.slice(at + 2 + message.length);
};

/**
 * RUM transmits `message` and `stack` verbatim, and a render error's message
 * can carry note text (tiptap/ProseMirror interpolate the document into
 * theirs) or a signed URL (chunk loads). Keep the class and the stack frames,
 * which are what triage groups on.
 */
const toReportableError = (error: unknown, boundary: string): Error => {
  const isError = error instanceof Error;
  const name = isError && error.name ? error.name : 'NonError';
  const reportable = new Error(`${name} caught by ${boundary} boundary`);
  reportable.name = name;
  reportable.stack =
    isError && typeof error.stack === 'string'
      ? redactStackHeader(error.stack, error.message, reportable.message)
      : undefined;
  return reportable;
};

/**
 * The tags are what separate a crash from the handled `addError` calls that
 * share `@error.source:custom`. The message is replaced, so a known message
 * shape is reported as `error_class` plus its identifiers. Takes `unknown`:
 * React hands a boundary whatever was thrown.
 */
export const reportBoundaryError = (
  error: unknown,
  { boundary, fatal, recoveredByReload = false, context }: BoundaryErrorReport
): void => {
  datadogRum.addError(toReportableError(error, boundary), {
    ...context,
    ...classifyBoundaryError(error),
    error_boundary: {
      name: boundary,
      fatal,
      recovered_by_reload: recoveredByReload,
    },
  });
};
